/* eslint-disable no-console */
import {
  GAME_CONFIG,
  BUILDINGS,
  Building,
  BuildingType,
  ItemType,
  BeltItem,
} from './constants';
import {
  GameMap,
  BuildingLayer,
  BeltContentLayer,
  getDirectionalBuildingCharacter,
  getBuildingPositions,
  findBuildingByCharacter,
} from './GameState';

// Test result types
export interface TestResult {
  name: string;
  passed: boolean;
  message: string;
  duration: number;
  details?: Record<string, unknown>;
}

// Test function type
export type TestFunction = (world: TestWorld) => Promise<TestResult>;

// Test definition interface
export interface TestDefinition {
  name: string;
  description: string;
  testFunction: TestFunction;
}

// Test world class for creating controlled test environments
export class TestWorld {
  public gameMap: GameMap = {};
  public buildingLayer: BuildingLayer = {};
  public beltContentLayer: BeltContentLayer = {};
  public width: number;
  public height: number;
  private simulationTick = 0;

  constructor(width: number = 100, height: number = 100) {
    this.width = width;
    this.height = height;
    this.initializeEmptyWorld();
  }

  // Initialize an empty world
  private initializeEmptyWorld(): void {
    for (let y = 0; y < this.height; y++) {
      for (let x = 0; x < this.width; x++) {
        const key = `${x},${y}`;
        // Create borders
        if (
          x === 0 ||
          x === this.width - 1 ||
          y === 0 ||
          y === this.height - 1
        ) {
          this.gameMap[key] = GAME_CONFIG.WALL_CHARACTER;
        } else {
          this.gameMap[key] = GAME_CONFIG.EMPTY_CHARACTER;
        }
      }
    }
  }

  // Place a resource node at specific coordinates
  public placeResourceNode(
    x: number,
    y: number,
    nodeType: 'copper' | 'iron'
  ): void {
    const key = `${x},${y}`;
    const character =
      nodeType === 'copper'
        ? GAME_CONFIG.COPPER_NODE_CHARACTER
        : GAME_CONFIG.IRON_NODE_CHARACTER;

    this.gameMap[key] = character;
  }

  // Place a building at specific coordinates
  public placeBuilding(
    x: number,
    y: number,
    buildingId: BuildingType,
    facing: 'up' | 'down' | 'left' | 'right' = 'right'
  ): boolean {
    const building = Object.values(BUILDINGS).find(b => b.id === buildingId);
    if (!building) {
      throw new Error(`Building with id '${buildingId}' not found`);
    }

    // Get all positions this building will occupy
    const buildingPositions = getBuildingPositions(x, y, building);

    // Check if all positions are valid and empty
    for (const pos of buildingPositions) {
      const key = `${pos.x},${pos.y}`;

      // Check bounds
      if (
        pos.x < 0 ||
        pos.x >= this.width ||
        pos.y < 0 ||
        pos.y >= this.height
      ) {
        return false;
      }

      // Check if position is already occupied by a building
      if (this.buildingLayer[key]) {
        return false;
      }

      // For miners, check if they're being placed on resource nodes
      if (
        building.category === 'mining' &&
        building.requiresResourceNode !== false
      ) {
        const terrain = this.gameMap[key];
        if (
          terrain !== GAME_CONFIG.IRON_NODE_CHARACTER &&
          terrain !== GAME_CONFIG.COPPER_NODE_CHARACTER
        ) {
          return false;
        }
      }
    }

    // Place the building
    const buildingCharacter = getDirectionalBuildingCharacter(building, facing);
    for (const pos of buildingPositions) {
      const key = `${pos.x},${pos.y}`;
      this.buildingLayer[key] = buildingCharacter;
    }

    return true;
  }

  // Get the tile at a specific position (including buildings)
  public getTile(x: number, y: number): string | undefined {
    const key = `${x},${y}`;

    // Check building layer first
    if (this.buildingLayer[key]) {
      return this.buildingLayer[key];
    }

    // Then check terrain
    return this.gameMap[key];
  }

  // Get building info at position
  public getBuildingAt(x: number, y: number): Building | null {
    const key = `${x},${y}`;
    const buildingCharacter = this.buildingLayer[key];
    if (!buildingCharacter) {
      return null;
    }
    return findBuildingByCharacter(buildingCharacter);
  }

  // Add an item to a belt at specific position
  public addItemToBelt(
    x: number,
    y: number,
    itemType: ItemType,
    progress: number = 0
  ): boolean {
    const key = `${x},${y}`;
    const building = this.getBuildingAt(x, y);

    if (!building || (building.id !== 'belt' && building.id !== 'fast_belt')) {
      return false;
    }

    const currentItems = this.beltContentLayer[key] || [];
    const existingStack = currentItems[0];
    const existingCount = existingStack?.count ?? (existingStack ? 1 : 0);

    if (!existingStack) {
      const beltItem: BeltItem = {
        item: itemType,
        progress,
        id: `${this.simulationTick}-${Math.random().toString(36).substr(2, 9)}`,
        count: 1,
      };
      this.beltContentLayer[key] = [beltItem];
      return true;
    }

    if (existingStack.item !== itemType) {
      return false;
    }

    this.beltContentLayer[key] = [
      {
        ...existingStack,
        count: existingCount + 1,
      },
    ];
    return true;
  }

  // Get items on belt at position
  public getItemsOnBelt(x: number, y: number): BeltItem[] {
    const key = `${x},${y}`;
    return this.beltContentLayer[key] || [];
  }

  // Simulate one tick of the game (for testing belt movement, etc.)
  public simulateTick(): void {
    this.simulationTick++;
    this.simulateBeltMovement();
    this.simulateMinerProduction();
  }

  // Simulate belt item movement
  private simulateBeltMovement(): void {
    const newBeltContentLayer: BeltContentLayer = {};

    for (const [posKey, items] of Object.entries(this.beltContentLayer)) {
      if (!items || items.length === 0) continue;

      const coords = posKey.split(',').map(Number);
      if (
        coords.length !== 2 ||
        coords[0] === undefined ||
        coords[1] === undefined
      )
        continue;
      const [x, y] = coords;
      const building = this.getBuildingAt(x, y);

      if (
        !building ||
        (building.id !== 'belt' && building.id !== 'fast_belt')
      ) {
        continue;
      }

      // Get belt direction
      const beltCharacter = this.buildingLayer[posKey];
      if (!beltCharacter) {
        throw new Error(`No building character found at position ${posKey}`);
      }
      let direction: 'up' | 'down' | 'left' | 'right';

      if (beltCharacter === '→' || beltCharacter === '⇒') direction = 'right';
      else if (beltCharacter === '←' || beltCharacter === '⇐')
        direction = 'left';
      else if (beltCharacter === '↑' || beltCharacter === '⇑') direction = 'up';
      else if (beltCharacter === '↓' || beltCharacter === '⇓')
        direction = 'down';
      else continue;

      // Calculate speed
      const speed = building.id === 'fast_belt' ? 0.2 : 0.1;

      for (const originalItem of items) {
        const stackCount = originalItem.count ?? 1;
        const item =
          originalItem.count === stackCount
            ? originalItem
            : { ...originalItem, count: stackCount };
        const newProgress = item.progress + speed;

        if (newProgress >= 1.0) {
          // Item moves to next belt
          let nextX = x,
            nextY = y;
          switch (direction) {
            case 'right':
              nextX = x + 1;
              break;
            case 'left':
              nextX = x - 1;
              break;
            case 'up':
              nextY = y - 1;
              break;
            case 'down':
              nextY = y + 1;
              break;
          }

          const nextKey = `${nextX},${nextY}`;
          const nextBuilding = this.getBuildingAt(nextX, nextY);

          if (
            nextBuilding &&
            (nextBuilding.id === 'belt' || nextBuilding.id === 'fast_belt')
          ) {
            const destinationOccupied =
              (newBeltContentLayer[nextKey]?.length ?? 0) > 0;

            if (!destinationOccupied) {
              newBeltContentLayer[nextKey] = [
                {
                  ...item,
                  progress: 0,
                  count: stackCount,
                },
              ];
              continue;
            }
          }

          if (!newBeltContentLayer[posKey]) {
            newBeltContentLayer[posKey] = [];
          }
          newBeltContentLayer[posKey].push({
            ...item,
            progress: 0.99,
            count: stackCount,
          });
        } else {
          // Item continues on current belt
          if (!newBeltContentLayer[posKey]) {
            newBeltContentLayer[posKey] = [];
          }
          newBeltContentLayer[posKey].push({
            ...item,
            progress: newProgress,
            count: stackCount,
          });
        }
      }
    }

    this.beltContentLayer = newBeltContentLayer;
  }

  // Simulate miner production
  private simulateMinerProduction(): void {
    for (const [posKey, buildingChar] of Object.entries(this.buildingLayer)) {
      const building = findBuildingByCharacter(buildingChar);
      if (!building || building.category !== 'mining') continue;

      const coords = posKey.split(',').map(Number);
      if (
        coords.length !== 2 ||
        coords[0] === undefined ||
        coords[1] === undefined
      )
        continue;
      const [x, y] = coords;
      const terrain = this.gameMap[posKey];

      // Determine what resource to produce
      let itemType: ItemType;
      if (terrain === GAME_CONFIG.COPPER_NODE_CHARACTER) {
        itemType = 'copper-ore';
      } else if (terrain === GAME_CONFIG.IRON_NODE_CHARACTER) {
        itemType = 'iron-ore';
      } else {
        continue;
      }

      // Try to output to adjacent belt (simplified - just check right side)
      const outputX = x + 1;
      const outputY = y;
      const outputBuilding = this.getBuildingAt(outputX, outputY);

      if (
        outputBuilding &&
        (outputBuilding.id === 'belt' || outputBuilding.id === 'fast_belt')
      ) {
        // Check if belt has space (simplified - max 2 items per belt tile)
        this.addItemToBelt(outputX, outputY, itemType, 0);
      }
    }
  }

  // Count items of specific type on all belts
  public countItemsOnBelts(itemType?: ItemType): number {
    let count = 0;
    for (const items of Object.values(this.beltContentLayer)) {
      if (items) {
        for (const item of items) {
          if (!itemType || item.item === itemType) {
            count += item.count ?? 1;
          }
        }
      }
    }
    return count;
  }

  // Get a visual representation of the world (for debugging)
  public getVisualRepresentation(): string {
    const lines: string[] = [];
    for (let y = 0; y < this.height; y++) {
      let line = '';
      for (let x = 0; x < this.width; x++) {
        const tile = this.getTile(x, y) || ' ';
        line += tile;
      }
      lines.push(line);
    }
    return lines.join('\n');
  }
}

// Test runner class
export class TestRunner {
  private tests: TestDefinition[] = [];
  private results: TestResult[] = [];

  // Register a test
  public addTest(test: TestDefinition): void {
    this.tests.push(test);
  }

  // Run all tests
  public async runAllTests(): Promise<TestResult[]> {
    this.results = [];

    console.log(
      `🧪 Running ${this.tests.length} test${this.tests.length !== 1 ? 's' : ''}...\n`
    );

    for (const test of this.tests) {
      console.log(`Running: ${test.name}`);
      const startTime = performance.now();

      try {
        const world = new TestWorld();
        const result = await test.testFunction(world);
        result.duration = performance.now() - startTime;
        this.results.push(result);

        const status = result.passed ? '✅ PASS' : '❌ FAIL';
        console.log(
          `${status}: ${test.name} (${result.duration.toFixed(2)}ms)`
        );
        if (!result.passed) {
          console.log(`   ${result.message}`);
        }
      } catch (error) {
        const duration = performance.now() - startTime;
        const result: TestResult = {
          name: test.name,
          passed: false,
          message: `Test threw an error: ${error instanceof Error ? error.message : String(error)}`,
          duration,
        };
        this.results.push(result);
        console.log(`❌ ERROR: ${test.name} (${duration.toFixed(2)}ms)`);
        console.log(`   ${result.message}`);
      }
    }

    this.printSummary();
    return this.results;
  }

  // Print test summary
  private printSummary(): void {
    const passed = this.results.filter(r => r.passed).length;
    const failed = this.results.length - passed;
    const totalTime = this.results.reduce((sum, r) => sum + r.duration, 0);

    console.log('\n📊 Test Summary:');
    console.log(`   Total: ${this.results.length}`);
    console.log(`   Passed: ${passed} ✅`);
    console.log(`   Failed: ${failed} ${failed > 0 ? '❌' : ''}`);
    console.log(`   Time: ${totalTime.toFixed(2)}ms`);

    if (failed === 0) {
      console.log('\n🎉 All tests passed!');
    } else {
      console.log(`\n💥 ${failed} test${failed !== 1 ? 's' : ''} failed.`);
    }
  }

  // Get test results
  public getResults(): TestResult[] {
    return [...this.results];
  }
}

// Helper function to create assertions
export const assert = {
  isTrue: (condition: boolean, message: string): void => {
    if (!condition) {
      throw new Error(`Assertion failed: ${message}`);
    }
  },

  equals: <T>(actual: T, expected: T, message: string): void => {
    if (actual !== expected) {
      throw new Error(
        `Assertion failed: ${message}. Expected ${expected}, got ${actual}`
      );
    }
  },

  greaterThan: (actual: number, expected: number, message: string): void => {
    if (actual <= expected) {
      throw new Error(
        `Assertion failed: ${message}. Expected ${actual} > ${expected}`
      );
    }
  },

  contains: <T>(array: T[], item: T, message: string): void => {
    if (!array.includes(item)) {
      throw new Error(
        `Assertion failed: ${message}. Array does not contain ${item}`
      );
    }
  },
};
